<template>
  <div
    class="ds-campaigns-hero"
    :class="`ds-campaigns-hero--${supportedTheme}`"
  >
    <div class="ds-campaigns-hero__container">
      <!-- Media -->
      <div
        v-parallax-scroll-awds
        aria-hidden="true"
        class="ds-campaigns-hero__media-container"
      >
        <DsImageElement
          v-if="isImageRendered"
          :image="isScreenLarge && desktopImage ? desktopImage : image"
          :screen="screen"
          class="ds-campaigns-hero__media"
          :class="{
            'ds-campaigns-hero__media--zoom': ready
          }"
        />
        <DsVideoElement
          v-else-if="isVideoRendered"
          v-bind="isScreenLarge && desktopVideo ? desktopVideo : video"
          :key="videoKey"
          :screen="screen"
          class="ds-campaigns-hero__media"
          :class="{
            'ds-campaigns-hero__media--zoom': ready
          }"
        />
      </div>
      <!-- Content -->
      <div class="ds-grid ds-campaigns-hero__content-scroller">
        <div
          class="ds-grid__col ds-grid__col--no-gutter ds-grid__col--12 ds-md-grid__col--6 ds-lg-grid__col--5 ds-padding-x-12 ds-md-padding-x-24 ds-campaigns-hero__content-box"
          :class="{
            'ds-campaigns-hero__content-box--fade': ready
          }"
        >
          <div
            class="ds-campaigns-hero__box-container ds-padding-x-12 ds-padding-y-48 ds-md-padding-48 ds-lg-padding-96"
          >
            <!-- Eyebrow -->
            <DsEyebrow
              v-if="eyebrow"
              :icon="icon"
              :text="eyebrow"
              :theme="supportedTheme"
              :has-outer-padding="false"
              class="ds-padding-bottom-12 ds-campaigns-hero__box-item"
            />
            <!-- Title -->
            <h1
              v-if="splittedTitle"
              class="ds-campaigns-hero__box-item-title-container ds-heading-large ds-margin-bottom-12"
            >
              <!-- Accessibility Title -->
              <span class="ds-sr-only">{{ title }}</span>
              <span
                v-for="(word, index) in splittedTitle"
                :key="index"
                aria-hidden="true"
                class="ds-campaigns-hero__box-item-title"
              >
                {{ word }}
              </span>
            </h1>
            <!-- Description -->
            <div class="ds-campaigns-hero__box-item">
              <slot name="description">
                <div
                  v-if="description"
                  class="ds-rich-text ds-text-medium"
                >
                  {{ description }}
                </div>
              </slot>
            </div>
            <!-- Links -->
            <div
              v-if="primaryLink || secondaryLink"
              class="ds-buttons-wrapper ds-campaigns-hero__box-item ds-margin-top-24"
            >
              <DsLink
                :url="primaryLink.url"
                :open-in-new-window="primaryLink.openInNewWindow"
                :nuxt="primaryLink.nuxt"
                :content="primaryLink.title"
                :tracking-data="{ 'component-name': $options.name, text: primaryLink.title, title: title }"
                type="button"
                variant="primary"
              />
              <DsLink
                v-if="secondaryLink"
                :url="secondaryLink.url"
                :open-in-new-window="secondaryLink.openInNewWindow"
                :nuxt="secondaryLink.nuxt"
                :content="secondaryLink.title"
                :icon-right="{ category: 'arrows', name: 'chevron-right-small' }"
                :tracking-data="{ 'component-name': $options.name, text: secondaryLink.title, title: title }"
                variant="secondary"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-ignore wait for awds to be typed
import { DsImageElement, DsVideoElement, DsLink, DsEyebrow, vParallaxScrollAwds } from 'awds'
// typed locally via 'declare module'
import type { ThemeType } from 'awds'

const { isScreenLarge, screen } = useScreen()
const { $gsap, $CustomEase } = useNuxtApp()

defineOptions({
  name: 'AnimatedHero'
})

type HeroCampaignsProps = {
  /** @default '' */
  title?: string | null
  /** @default '' */
  eyebrow?: string | null
  /**
   * TODO: define the real image object structure
   * @default null */
  image?: object | null
  /**
   * TODO: define the real image object structure
   * @default null
   */
  desktopImage?: object | null
  /** @default null */
  aspectRatio?: string | null
  /**
   * TODO: define the real image object structure
   * @default null
   */
  icon?: object | null
  video?: object | null
  desktopVideo?: object | null
  /** @default '' */
  description?: string
  links: any[]
}

const {
  title = '',
  eyebrow = '',
  image = null,
  desktopImage = null,
  aspectRatio = null,
  icon = null,
  video = null,
  desktopVideo = null,
  description = '',
  links,
  theme
} = defineProps<
  HeroCampaignsProps & {
    theme: ThemeType
  }
>()

const primaryLink = computed(() => links?.[0] || null)
const secondaryLink = computed(() => links?.[1] || null)
const splittedTitle = computed(() => title?.split(' '))
const supportedTheme = computed(() => {
  const supportedTheme = ['light', 'dark'].find((x) => x === theme) as ThemeType
  if (!supportedTheme) return 'light' as ThemeType

  return supportedTheme
})
const isImageRendered = computed(() => {
  if (isScreenLarge.value && desktopImage) return !!desktopImage
  if (!isScreenLarge.value && image) return !!image
  if (isScreenLarge.value && image && !desktopVideo) return !!image

  return false
})
const isVideoRendered = computed(() => {
  if (isScreenLarge.value && desktopVideo) return !!desktopVideo
  if (!isScreenLarge.value && video) return !!video
  if (isScreenLarge.value && video && !desktopImage) return !!video

  return false
})
const videoKey = computed(() => {
  return `video-screen-large-is-${isScreenLarge.value}`
})

const ready = ref<boolean | null>(null)

onMounted(async () => {
  await nextTick()
  ready.value = true

  // Count the lines based on their Y position on the page
  // Update the counter whenever line changes
  // The counter will serve as the stagger animation delay for next lines
  let lineYPosition: number
  let counter = 0
  $gsap.fromTo(
    '.ds-campaigns-hero__box-item-title',
    {
      y: 50,
      opacity: 0
    },
    {
      y: 0,
      opacity: 1,
      duration: 0.8,
      delay: 1.1,
      ease: $CustomEase.create('custom', 'M0,0 C0.257,0 0.032,1 1, 1'),
      // TODO: reuse this function, parameter is `counter` divisor
      stagger: function (_, target) {
        const yPosition = target.getBoundingClientRect().y
        if (!lineYPosition) {
          lineYPosition = yPosition
        }

        if (yPosition > lineYPosition) {
          counter++
          lineYPosition = yPosition
        }

        return counter / 8
      }
    }
  )

  // Description and links animation
  $gsap.fromTo(
    '.ds-campaigns-hero__box-item',
    {
      opacity: 0
    },
    {
      opacity: 1,
      duration: 1,
      delay: 1.7,
      stagger: 0.2
    }
  )
})
</script>

<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';
@import 'awds/foundation-mixins.scss';

$animation-zoom-in-duration: 1.2s;
$animation-duration: 1s;

.ds-campaigns-hero {
  $this: &;
  position: relative;
  overflow: hidden;
  width: 100%;

  @include wds-screen-only-small() {
    padding-bottom: 500px;
  }

  &__media-container {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;

    #{$this}__media {
      height: 100%;

      :deep() {
        img {
          opacity: 0;
          object-fit: cover;
        }

        video {
          opacity: 0;
          height: 100%;
          width: 100%;
          object-fit: cover;
        }
      }

      &--zoom {
        :deep() {
          img {
            animation: media-zoom $animation-zoom-in-duration cubic-bezier(0.425, 0.005, 0.105, 1) forwards;
          }

          video {
            animation: media-zoom $animation-zoom-in-duration cubic-bezier(0.425, 0.005, 0.105, 1) forwards;
          }
        }
      }
    }
  }

  &__container {
    #{$this}__content-scroller {
      max-width: 1800px;
      position: relative;
      scrollbar-width: none;
      min-height: calc(100vh - 72px);
      min-height: calc(100dvh - 72px);

      @include wds-screen-only-small() {
        min-height: unset;
      }

      &::-webkit-scrollbar {
        display: none;
      }

      #{$this}__content-box {
        opacity: 0;
        margin-block: $spacing-24;

        @include wds-screen-only-small() {
          margin-block: $spacing-12;
        }

        &--fade {
          animation: fade-in $animation-duration 0.7s forwards;
        }

        #{$this}__box-container {
          height: 100%;

          #{$this}__box-item-title-container {
            display: flex;
            flex-wrap: wrap;
            column-gap: 6px;
          }

          #{$this}__box-item-title {
            display: inline-block;
          }
        }
      }
    }
  }

  // @TODO: review after theming revamp
  &--light {
    #{$this}__box-container {
      background-color: $color-grey-000;
      color: $color-grey-3200;
    }
  }

  &--dark {
    #{$this}__box-container {
      background-color: $color-grey-3200;
      color: $color-grey-000;
    }
  }
}

@keyframes media-zoom {
  from {
    opacity: 0;
    transform: scale(1.1);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
